import { useMemo } from "react";

import type { SearchDialogSurface } from "commonComponents/utilities/MainNavigation/SearchDialog/searchDialog.learn";

import type { ISearchHit } from "../types";

interface IAiSearchFeedbackSubject {
    /**
     * Which surface the feedback was given from, by its name in search
     * telemetry so the two can be joined.
     */
    surface: SearchDialogSurface;
    searchId: string;
    traceId: string | null;
    /** What the user searched or asked. */
    query: string;
    results: readonly ISearchHit[] | null;
}

/**
 * The extra info a `searchUxRedesign` surface sends with `AiSearch` feedback,
 * alike from each so the feedback reads the same wherever it came from. Keyed
 * `surface` like `AccountingSyncMoment`, the other target filed from more than
 * one place.
 *
 * The flag-off dropdown (`AiSearch`) builds its own payload and deliberately
 * sends no `surface`, so its feedback keeps one shape across its history; a
 * record without `surface` is the dropdown's. New `AiSearch` feedback goes
 * through this hook so that stays true.
 */
export function useAiSearchFeedbackExtraInfo(subject: IAiSearchFeedbackSubject): string {
    const { surface, searchId, traceId, query, results } = subject;
    return useMemo(() => {
        const hits = results ?? [];
        return JSON.stringify({
            surface,
            searchId,
            traceId,
            query,
            resultCount: hits.length,
            topResults: hits.slice(0, 10).map((r) => ({
                entity: r.entity,
                id: r.id,
                summary: r.summary,
            })),
        });
    }, [surface, searchId, traceId, query, results]);
}
